import * as React from 'react';

import { CodeEditor, Select, Switch, TextField } from '@rematter/ui-web';

import { ExpressionInput } from '../../../shared/ExpressionInput';
import { KeyValueEditor } from '../../../shared/KeyValueEditor';
import { NodeConfigProps } from '../../types';

import { HttpActionConfigType } from './schema';

/**
 * Configuration panel for HTTP actions.
 * Allows configuring URL, method, headers, body, and retry settings.
 */
export const HttpActionConfig: React.FC<
  NodeConfigProps<HttpActionConfigType>
> = ({ config, onUpdate }) => {
  const [url, setUrl] = React.useState<string>(config.url || '');
  const [method, setMethod] = React.useState<string>(config.method || 'POST');
  const [headers, setHeaders] = React.useState<Record<string, string>>(
    config.headers || {},
  );
  const [body, setBody] = React.useState<string>(config.body || '');
  const [bodyType, setBodyType] = React.useState<string>(
    config.bodyType || 'json',
  );
  const [timeout, setTimeout] = React.useState<string>(
    config.timeout?.toString() || '30000',
  );
  const [retryCount, setRetryCount] = React.useState<string>(
    config.retryCount?.toString() || '3',
  );
  const [continueOnFail, setContinueOnFail] = React.useState<boolean>(
    config.continueOnFail || false,
  );

  const handleUpdate = React.useCallback(
    (updates: Partial<HttpActionConfigType>) => {
      onUpdate({ ...config, ...updates });
    },
    [config, onUpdate],
  );

  const handleUrlChange = React.useCallback(
    (value: string) => {
      setUrl(value);
      handleUpdate({ url: value });
    },
    [handleUpdate],
  );

  const handleMethodChange = React.useCallback(
    (value: string) => {
      setMethod(value);
      handleUpdate({ method: value as HttpActionConfigType['method'] });
    },
    [handleUpdate],
  );

  const handleHeadersChange = React.useCallback(
    (value: Record<string, string>) => {
      setHeaders(value);
      handleUpdate({ headers: value });
    },
    [handleUpdate],
  );

  const handleBodyChange = React.useCallback(
    (value: string) => {
      setBody(value);
      handleUpdate({ body: value });
    },
    [handleUpdate],
  );

  const handleBodyTypeChange = React.useCallback(
    (value: string) => {
      setBodyType(value);
      handleUpdate({ bodyType: value as 'json' | 'form' | 'text' });
    },
    [handleUpdate],
  );

  const handleTimeoutChange = React.useCallback(
    (value: string) => {
      setTimeout(value);
      const numValue = parseInt(value, 10);
      if (!isNaN(numValue)) {
        handleUpdate({ timeout: numValue });
      }
    },
    [handleUpdate],
  );

  const handleRetryCountChange = React.useCallback(
    (value: string) => {
      setRetryCount(value);
      const numValue = parseInt(value, 10);
      if (!isNaN(numValue)) {
        handleUpdate({ retryCount: numValue });
      }
    },
    [handleUpdate],
  );

  const handleContinueOnFailChange = React.useCallback(
    (value: boolean) => {
      setContinueOnFail(value);
      handleUpdate({ continueOnFail: value });
    },
    [handleUpdate],
  );

  return (
    <div className="@HttpActionConfig space-y-4">
      <div>
        <ExpressionInput
          label="URL"
          value={url}
          onChange={handleUrlChange}
          placeholder="https://api.example.com/endpoint"
          description="HTTP endpoint URL (supports expressions)"
        />
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium">Method</label>
        <Select
          placeholder="Select HTTP method..."
          value={method}
          onChange={handleMethodChange}
          options={[
            { key: 'GET', label: 'GET' },
            { key: 'POST', label: 'POST' },
            { key: 'PUT', label: 'PUT' },
            { key: 'PATCH', label: 'PATCH' },
            { key: 'DELETE', label: 'DELETE' },
          ]}
        />
      </div>

      <div>
        <KeyValueEditor
          label="Headers"
          value={headers}
          onChange={handleHeadersChange}
          keyPlaceholder="Header name"
          valuePlaceholder="Header value"
          description="HTTP headers to include in the request"
        />
      </div>

      {method !== 'GET' && (
        <>
          <div>
            <label className="mb-1 block text-sm font-medium">Body Type</label>
            <Select
              placeholder="Select body type..."
              value={bodyType}
              onChange={handleBodyTypeChange}
              options={[
                { key: 'json', label: 'JSON' },
                { key: 'form', label: 'Form Data' },
                { key: 'text', label: 'Plain Text' },
              ]}
            />
          </div>

          <CodeEditor
            label="Body"
            value={body}
            onChange={handleBodyChange}
            language={bodyType === 'json' ? 'json' : 'text'}
            placeholder={
              bodyType === 'json'
                ? '{"key": "value"}'
                : 'Request body content...'
            }
            description="Request body (supports expressions with {{ }} syntax)"
            height="150px"
            enableInterpolation
          />
        </>
      )}

      <div className="border-t pt-4">
        <p className="mb-3 text-sm font-medium">Advanced Settings</p>

        <div className="space-y-3">
          <TextField
            label="Timeout (ms)"
            value={timeout}
            onChange={handleTimeoutChange}
            placeholder="30000"
            type="number"
            description="Request timeout in milliseconds"
          />

          <TextField
            label="Retry Count"
            value={retryCount}
            onChange={handleRetryCountChange}
            placeholder="3"
            type="number"
            description="Number of retry attempts on failure"
          />
        </div>
      </div>

      <div className="border-t pt-4">
        <p className="mb-3 text-sm font-medium">Error Handling</p>

        <div className="rounded-lg border bg-neutral/5 p-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm">Continue on failure</p>
              <p className="text-xs text-neutral/60">
                If enabled, the workflow will continue even if this action fails
              </p>
            </div>
            <Switch
              isSelected={continueOnFail}
              onChange={handleContinueOnFailChange}
            />
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-primary/20 bg-primary/10 p-3">
        <p className="text-xs text-primary">
          <strong>Tip:</strong> Use {'{{trigger.data}}'} to send the entire
          trigger payload, or specific fields like {'{{trigger.data.orderId}}'}.
        </p>
      </div>
    </div>
  );
};
